import React, { FC, useContext, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { MapListingContext, ViewTriggerButtonPositions } from '../context/MapListingContext';
import { isBrowser } from '../utils/isBrowser';
import { Button } from './Button';
import { ListView } from './icon/icons/ListView';
import { MapView } from './icon/icons/MapView';

const StyledStickyViewContainer = styled.div`
  position: relative;
  min-height: 100vh; // cls fix
`;

interface StyledStickyViewToggleButtonProps {
  stickyViewTogglePosition: ViewTriggerButtonPositions;
  stickyViewToggleVisibility: boolean;
}

const StyledStickyViewToggleButton = styled(Button)<StyledStickyViewToggleButtonProps>`
  ${({ theme: { space, media, shadows, layers }, stickyViewToggleVisibility, stickyViewTogglePosition }) => css`
    opacity: ${stickyViewToggleVisibility ? '1' : '0'};
    position: ${stickyViewTogglePosition};
    position: fixed;
    bottom: ${space.lg};
    left: 50%;
    transform: translateX(-50%);
    box-shadow: ${shadows.small};
    z-index: ${layers.toggleButton};

    // Tablet > styles

    @media (min-width: ${media.md}) {
      display: none;
    }
  `};
`;

export const StickyViewContainer: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const {
    activeView,
    setActiveView,
    stickyViewContainerRef,
    stickyViewToggleRef,
    setStickyViewTogglePosition,
    setStickyViewToggleVisibility,
    stickyViewTogglePosition,
    stickyViewToggleVisibility,
  } = useContext(MapListingContext);

  // Setting the mobile view toggle states (visible/position) based on scroll

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (isBrowser && stickyViewContainerRef.current) {
        const containerHeight = stickyViewContainerRef.current?.offsetHeight;
        const windowHeight = window.innerHeight;

        if (entry.isIntersecting) {
          setStickyViewTogglePosition('absolute');

          if (containerHeight > windowHeight) {
            setStickyViewToggleVisibility(false);
          }
        } else {
          setStickyViewTogglePosition('sticky');
          setStickyViewToggleVisibility(true);
        }
      }
    });
    if (stickyViewToggleRef.current) {
      observer.observe(stickyViewToggleRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [stickyViewToggleRef]);

  const toggleActiveView = () => {
    setActiveView(activeView === 'map' ? 'list' : 'map');
  };

  return (
    <StyledStickyViewContainer ref={stickyViewContainerRef}>
      {children}
      <div ref={stickyViewToggleRef} />
      <StyledStickyViewToggleButton
        stickyViewTogglePosition={stickyViewTogglePosition}
        stickyViewToggleVisibility={stickyViewToggleVisibility}
        iconRight={activeView === 'list' ? <MapView /> : <ListView />}
        onClick={toggleActiveView}
      >
        {activeView === 'list' ? `Map View` : `List View`}
      </StyledStickyViewToggleButton>
    </StyledStickyViewContainer>
  );
};
